/**
 * Credit Note Firestore Service
 * Handles credit note operations with Firestore
 */

import { db } from './firebase';
import {
  collection,
  doc,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  orderBy,
  limit,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import {
  generateCreditNoteNumber,
  createCreditNoteData,
  getAvailableCredit,
} from './creditNoteService';

/**
 * Create a new credit note
 *
 * @param {string} userId - User ID
 * @param {Object} data - Credit note data
 * @returns {Promise<{id: string, creditNoteNumber: string}>}
 */
export async function createCreditNote(userId, data) {
  if (!db) {
    throw new Error('Firebase not configured');
  }

  // Get existing credit note numbers for this user
  const creditNotesRef = collection(db, 'creditNotes');
  const q = query(creditNotesRef, where('userId', '==', userId));
  const snapshot = await getDocs(q);

  const existingNumbers = snapshot.docs.map((doc) => doc.data().creditNoteNumber);
  const creditNoteNumber = generateCreditNoteNumber(
    existingNumbers,
    new Date().getFullYear()
  );

  const creditNoteData = createCreditNoteData(
    {
      ...data,
      userId,
    },
    creditNoteNumber
  );

  // Add server timestamp
  creditNoteData.createdAt = serverTimestamp();
  creditNoteData.updatedAt = serverTimestamp();

  const docRef = await addDoc(creditNotesRef, creditNoteData);

  return {
    id: docRef.id,
    creditNoteNumber,
  };
}

/**
 * Get all credit notes for a user
 *
 * @param {string} userId - User ID
 * @returns {Promise<Array>} - Array of credit notes
 */
export async function getCreditNotes(userId) {
  if (!db) {
    return [];
  }

  const creditNotesRef = collection(db, 'creditNotes');
  const q = query(
    creditNotesRef,
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt?.toDate?.() || new Date(),
    issuedAt: doc.data().issuedAt?.toDate?.() || null,
    updatedAt: doc.data().updatedAt?.toDate?.() || null,
  }));
}

/**
 * Get credit notes for a specific client
 *
 * @param {string} clientId - Client ID
 * @returns {Promise<Array>} - Array of credit notes
 */
export async function getClientCreditNotes(clientId) {
  if (!db) {
    return [];
  }

  const creditNotesRef = collection(db, 'creditNotes');
  const q = query(
    creditNotesRef,
    where('clientId', '==', clientId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    createdAt: doc.data().createdAt?.toDate?.() || new Date(),
    issuedAt: doc.data().issuedAt?.toDate?.() || null,
  }));
}

/**
 * Get a single credit note by ID
 *
 * @param {string} creditNoteId - Credit note ID
 * @returns {Promise<Object|null>}
 */
export async function getCreditNoteById(creditNoteId) {
  if (!db) {
    return null;
  }

  const docRef = doc(db, 'creditNotes', creditNoteId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
    createdAt: docSnap.data().createdAt?.toDate?.() || new Date(),
    issuedAt: docSnap.data().issuedAt?.toDate?.() || null,
  };
}

/**
 * Issue a credit note (change status from draft to issued)
 *
 * @param {string} creditNoteId - Credit note ID
 * @returns {Promise<void>}
 */
export async function issueCreditNote(creditNoteId) {
  if (!db) {
    throw new Error('Firebase not configured');
  }

  const docRef = doc(db, 'creditNotes', creditNoteId);
  await updateDoc(docRef, {
    status: 'issued',
    issuedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Void a credit note
 *
 * @param {string} creditNoteId - Credit note ID
 * @param {string} reason - Reason for voiding
 * @returns {Promise<void>}
 */
export async function voidCreditNote(creditNoteId, reason = '') {
  if (!db) {
    throw new Error('Firebase not configured');
  }

  const docRef = doc(db, 'creditNotes', creditNoteId);
  await updateDoc(docRef, {
    status: 'void',
    voidedAt: serverTimestamp(),
    voidReason: reason,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Apply credit note to an invoice
 *
 * @param {string} creditNoteId - Credit note ID
 * @param {string} invoiceId - Invoice ID to apply credit to
 * @param {number} amount - Amount to apply
 * @returns {Promise<{appliedAmount: number, remainingCredit: number}>}
 */
export async function applyCreditToInvoice(creditNoteId, invoiceId, amount) {
  if (!db) {
    throw new Error('Firebase not configured');
  }

  // Get the credit note
  const creditNoteRef = doc(db, 'creditNotes', creditNoteId);
  const creditNoteSnap = await getDoc(creditNoteRef);

  if (!creditNoteSnap.exists()) {
    throw new Error('Credit note not found');
  }

  const creditNote = creditNoteSnap.data();

  // Check if credit note is issued
  if (creditNote.status !== 'issued') {
    throw new Error('Credit note must be issued before applying');
  }

  // Calculate available credit
  const available = getAvailableCredit({
    id: creditNoteId,
    ...creditNote,
  });

  if (amount > available) {
    throw new Error(`Insufficient credit. Available: ${available}`);
  }

  // Get the invoice
  const invoiceRef = doc(db, 'invoices', invoiceId);
  const invoiceSnap = await getDoc(invoiceRef);

  if (!invoiceSnap.exists()) {
    throw new Error('Invoice not found');
  }

  const invoice = invoiceSnap.data();

  // Update credit note
  const appliedTo = creditNote.appliedTo || [];
  appliedTo.push({
    invoiceId,
    invoiceNumber: invoice.invoiceNumber,
    amount,
    appliedAt: new Date(),
  });

  const newAppliedAmount = (creditNote.appliedAmount || 0) + amount;
  const newStatus = newAppliedAmount >= creditNote.amount ? 'applied' : 'issued';

  await updateDoc(creditNoteRef, {
    appliedTo,
    appliedAmount: newAppliedAmount,
    status: newStatus,
    updatedAt: serverTimestamp(),
  });

  // Update invoice
  const creditApplied = (invoice.creditApplied || 0) + amount;
  const newBalance = invoice.totalAmount - creditApplied - (invoice.paidAmount || 0);

  await updateDoc(invoiceRef, {
    creditApplied,
    creditNotes: [...(invoice.creditNotes || []), creditNoteId],
    balance: newBalance,
    updatedAt: serverTimestamp(),
  });

  return {
    appliedAmount: amount,
    remainingCredit: creditNote.amount - newAppliedAmount,
  };
}

/**
 * Get credit note summary for dashboard
 *
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Summary statistics
 */
export async function getCreditNoteSummary(userId) {
  if (!db) {
    return {
      total: 0,
      issued: 0,
      applied: 0,
      available: 0,
      count: 0,
    };
  }

  const creditNotesRef = collection(db, 'creditNotes');
  const q = query(creditNotesRef, where('userId', '==', userId));
  const snapshot = await getDocs(q);

  let total = 0;
  let issued = 0;
  let applied = 0;
  let available = 0;

  snapshot.docs.forEach((doc) => {
    const data = doc.data();
    total += data.amount || 0;

    if (data.status === 'issued') {
      issued += data.amount || 0;
      available += getAvailableCredit({ id: doc.id, ...data });
    } else if (data.status === 'applied') {
      applied += data.amount || 0;
    }
  });

  return {
    total,
    issued,
    applied,
    available,
    count: snapshot.size,
  };
}

/**
 * Delete a draft credit note
 *
 * @param {string} creditNoteId - Credit note ID
 * @returns {Promise<void>}
 */
export async function deleteCreditNote(creditNoteId) {
  if (!db) {
    throw new Error('Firebase not configured');
  }

  const docRef = doc(db, 'creditNotes', creditNoteId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    throw new Error('Credit note not found');
  }

  const creditNote = docSnap.data();

  if (creditNote.status !== 'draft') {
    throw new Error('Only draft credit notes can be deleted');
  }

  await deleteDoc(docRef);
}
